import React, { useEffect, useCallback } from "react";
import { IAudioPlayerProps } from "../../models/interfaces";
import { useHlsPlayback } from "../useHlsPlayback";
import "./style.css";

const AudioPlayer: React.FC<IAudioPlayerProps> = ({
	mediaUrl,
	durationSeconds = 0,
	onTimeUpdate,
	onPlayStateChange,
	onError,
	onReady,
	seekToTime,
	seekTrigger,
	onSeeked,
	pauseTrigger,
	playTrigger,
	presignedSegmentUrls,
}) => {
	const { mediaRef, isLoading, hasError, handleLoadedMetadataOrData, handleNativeError } = useHlsPlayback({
		mediaUrl,
		presignedSegmentUrls,
		onReady,
		onError,
	});

	const audioRef = mediaRef as React.RefObject<HTMLAudioElement>;

	useEffect(() => {
		if (seekToTime !== undefined && seekToTime !== null && audioRef.current) {
			audioRef.current.currentTime = seekToTime;
		}
	}, [seekToTime, seekTrigger]);

	useEffect(() => {
		if (pauseTrigger && audioRef.current && !audioRef.current.paused) {
			audioRef.current.pause();
		}
	}, [pauseTrigger]);

	useEffect(() => {
		if (playTrigger && audioRef.current && audioRef.current.paused) {
			audioRef.current.play().catch(() => {});
		}
	}, [playTrigger]);

	const handleTimeUpdate = useCallback(() => {
		if (!audioRef.current) return;
		onTimeUpdate?.(audioRef.current.currentTime);
	}, [onTimeUpdate]);

	const handleSeeked = useCallback(() => {
		if (!audioRef.current) return;
		const time = audioRef.current.currentTime;
		onTimeUpdate?.(time);
		onSeeked?.(time);
	}, [onTimeUpdate, onSeeked]);

	return (
		<div className="ap-container">
			{!mediaUrl || hasError ? (
				<div className="ap-error">
					<i className="material-symbols-outlined text-[--neutral-grey]" role="img" aria-label={!mediaUrl ? "Audio unavailable" : "Failed to load audio"}>
						error
					</i>
					<span className="text-[--neutral-grey] text-base-1">{!mediaUrl ? "Audio unavailable" : "Failed to load audio"}</span>
				</div>
			) : null}
			{isLoading && !hasError && mediaUrl && (
				<div className="ap-loading-overlay" role="status" aria-label="Loading audio">
					<div className="ap-buffering-spinner" />
				</div>
			)}
			<audio
				ref={audioRef}
				controls
				controlsList="nodownload"
				preload="auto"
				aria-label="Audio player"
				onTimeUpdate={handleTimeUpdate}
				onSeeked={handleSeeked}
				onLoadedMetadata={handleLoadedMetadataOrData}
				onError={handleNativeError}
				onPlay={() => onPlayStateChange?.(true)}
				onPause={() => onPlayStateChange?.(false)}
				onContextMenu={(e) => e.preventDefault()}
				style={{ display: isLoading || hasError || !mediaUrl ? "none" : undefined }}
			/>
		</div>
	);
};

export default React.memo(AudioPlayer);
